import Icon from "core/icon/Icon";
import "./InputFile.scss";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";

import React, { useEffect, useRef, useState } from "react";

const InputFile = ({
  accept = "application/pdf",
  allowDelete = true,
  icon,
  uploadIcon,
  isBottomIconClickable = true,
  required,
  onChange,
  value,
  previewUrl,
  onPreview,
  onDelete,
  readOnly = false,
}) => {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState(value || "");
  const [deleteButton, setDeleteButton] = useState(Boolean(value && allowDelete));

  useEffect(() => {
    setFileName(value || "");
    setDeleteButton(Boolean(value && allowDelete));
  }, [allowDelete, value]);

  const handleIconClick = () => {
    if (readOnly) return;

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const truncateFileName = (fullFileName, maxLength = 20) => {
    if (fullFileName.length <= maxLength) {
      return fullFileName;
    }
    const extension = fullFileName.split(".").pop();
    const truncated = fullFileName.substring(
      0,
      maxLength - extension.length - 3
    );
    return `${truncated}...${extension}`;
  };

  const handleFileChange = (event) => {
    if (readOnly) return;

    const file = event.target.files[0];
    if (file) {
      onChange(file);
    }
    event.target.value = "";
  };

  const handleDeleteClick = () => {
    if (readOnly) return;

    setFileName("");
    onDelete?.();
    setDeleteButton(false);
  };

  const renderFileName = () => {
    if (typeof onPreview === "function") {
      return (
        <button
          className="file-upload-name file-upload-trigger"
          onClick={onPreview}
          type="button"
        >
          {truncateFileName(fileName)}
        </button>
      );
    }
    if (previewUrl) {
      //Render the file name as a button when a preview handler is provided, so manager view can download the file on click.
      return (
        <a
          className="file-upload-name"
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          download={fileName}
        >
          {truncateFileName(fileName)}
        </a>
      );
    }
    return (
      <span className="file-upload-name">{truncateFileName(fileName)}</span>
    );
  };

  return (
    <div className="file-upload-container">
      {deleteButton && !readOnly && (
        <button
          className="file-delete-button"
          onClick={handleDeleteClick}
          type="button"
        >
          <FormattedMessage id={"delete"} defaultMessage={"Supprimer"} />
        </button>
      )}

      <Icon
        width={45}
        height={35}
        marginTop={7}
        icon={uploadIcon || icon}
        color="gray-200"
        className="add-icon"
        onClick={handleIconClick}
      />

      <input
        type="file"
        accept={accept}
        className="file-upload-input"
        onChange={handleFileChange}
        ref={fileInputRef}
        required={required}
        disabled={readOnly}
        style={{ opacity: 0 }}
      />
      <div className="content-section">
        {fileName ? (
          <div className="file-details">{renderFileName()}</div>
        ) : (
          <span className="no-file">Aucun fichier ajouté</span>
        )}
        <Icon
          marginRight={5}
          marginLeft={3}
          marginBottom={3}
          icon={icon}
          color="gray-200"
          className="file-icon-bottom"
          onClick={
            !readOnly && isBottomIconClickable ? handleIconClick : undefined
          }
          disabled={readOnly || !isBottomIconClickable}
        />
      </div>
    </div>
  );
};

export default React.memo(InputFile);

InputFile.propTypes = {
  accept: PropTypes.string,
  allowDelete: PropTypes.bool,
  icon: PropTypes.string,
  uploadIcon: PropTypes.string,
  isBottomIconClickable: PropTypes.bool,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.any,
  previewUrl: PropTypes.string,
  onPreview: PropTypes.func,
  onDelete: PropTypes.func,
  readOnly: PropTypes.bool,
};
